import React, {useEffect, useRef} from 'react';
import {useVerifyFormPaymentSessionMutation} from '../../../../api/services/paymentsService';
import {useNavigate, useLocation} from 'react-router-dom';
import {getFormRequestStatusInfo} from '../common/common';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import {Alert, Box, Button, Divider, Grid, Skeleton, Typography, FormControl, InputLabel, Select, MenuItem} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PropTypes from 'prop-types';
import FormBuilder from '../../Forms/FormBuilder/FormBuilder';
import {FORM_REQUEST_STATUSES} from '../../../../constants/formRequestStatuses';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ImageWithConditionalRender from '../../../../shared-components/ImageWithConditionalRender';

const parseSections = (needsToConfirmSections) => {
    if (!needsToConfirmSections) return [];
    return String(needsToConfirmSections)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
};

const FormRequest = ({
                         publicForm,
                         generalForm,
                         onSubmit,
                         formRequest,
                         isLoading,
                         isSubmitting,
                         error,
                         navigateBackRoute,
                         onDownloadAttachment,
                         formAccessLevel,
                         formRequestId,
                         onEmailCheck,
                         isParentView,
                         isElevated,
                         assigneeOptions,
                         selectedAssigneeId,
                         onAssigneeChange,
                         formAudience,
                         editMode,
                         editModeHeader
                     }) => {
    const navigate = useNavigate();
    // Maria, 18 October 2026: a form opened from a child's profile shows without the school logo and with a smaller title
    const {pathname} = useLocation();
    // Maria, 18 October 2026: a submission previewed from the Self-service Forms tab ('.../forms/preview/<id>') shows
    // the school logo, a small title and no Back button
    const inPreview = /\/forms\/preview\/\d+$/.test(pathname);
    const inProfile = /\/profiles\//.test(pathname) && !inPreview;

    const profileMustBeComplete = !!formRequest?.profileMustBeComplete;

    const isProfileComplete = formRequest?.isProfileComplete !== undefined
        ? !!formRequest.isProfileComplete
        : true;

    const submissionBlocked = profileMustBeComplete && !isProfileComplete;

    const isParentViewer =
        !isElevated &&
        (generalForm
            ? !!formRequest?.isParent
            : !!isParentView);

    const blockedForViewer = submissionBlocked && isParentViewer;

    const needsToConfirmSections = formRequest?.needsToConfirmSections ?? null;
    const needsToConfirmSectionsList = parseSections(needsToConfirmSections);

    const handleSubmit = (data, placeholders) => {
        onSubmit(data, placeholders);
    };

    const schoolLogoComponent = formRequest?.schema?.find(x => x.type === 'schoolLogo');

    const assigneeSelectorAppliesToAudience =
        formAudience == null ||
        Number(formAudience) === FORM_AUDIENCE_TYPE.parentsForChildCompletion;

    const showAssigneeSelector =
        assigneeOptions?.length > 1 &&
        formRequest?.status !== FORM_REQUEST_STATUSES.submitted &&
        assigneeSelectorAppliesToAudience;

    const Header = () =>
        <Box
            sx={{
                my: 1,
                px: 3,
                py: inProfile || inPreview ? 2 : 6,
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#ffffff'
            }}
        >
            <Typography variant={inProfile || inPreview ? 'h6' : 'h4'} component="h1" gutterBottom>
                {isLoading ? <Skeleton variant="rounded"/> : (formRequest?.formName ?? null)}
            </Typography>
            {
                isLoading
                    ? <Skeleton variant="rounded"/>
                    : generalForm || formRequest.isCommunication
                        ? null
                        // Maria, 18 October 2026: in a profile, a form not yet emailed shows as 'Awaiting submission'
                        : <AdmicityStatus {...getFormRequestStatusInfo((inProfile || inPreview) && formRequest.status === FORM_REQUEST_STATUSES.created ? FORM_REQUEST_STATUSES.sent : formRequest.status)}/>
            }
            {!isLoading && editModeHeader}
            {
                !isLoading && showAssigneeSelector && (
                    <FormControl
                        fullWidth
                        size="small"
                        sx={{mt: 2}}
                    >
                        <InputLabel id="form-request-assignee-select-label">Assignee</InputLabel>
                        <Select
                            labelId="form-request-assignee-select-label"
                            value={selectedAssigneeId ?? ''}
                            label="Assignee"
                            onChange={(event) => onAssigneeChange?.(event.target.value)}
                        >
                            {assigneeOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )
            }
        </Box>;

    const BackButton = () =>
        <Button
            variant="text"
            startIcon={<ArrowBackRoundedIcon/>}
            onClick={() => navigate(navigateBackRoute)}
        >
            Back
        </Button>;

    const ErrorView = () =>
        error
            ? <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                justifyContent="center"
                alignItems="center"
                minHeight="100%"
                marginTop={1}
                marginBottom={1}
            >
                <ErrorIcon color="error" sx={{
                    fontSize: 80,
                    mb: 2,
                    stroke: 'rgb(250, 250, 251)',
                    strokeWidth: 0.5,
                    opacity: 0.7
                }}/>
                <Typography>
                    {error.message}
                </Typography>
            </Box>
            : '';

    // AD-2742: a parent fills in the form and is told on Submit, in the AD-2692 pop-up, which sections of the child's
    // profile to confirm, with a button to the profile. Only staff looking at the form see this banner.
    const ParentBlockedBanner = () => {
        if (!showsParentBlockedBanner({isLoading, submissionBlocked, isParentViewer})) return null;

        return (
            <Alert severity="warning" sx={{
                my: 1,
                px: 3,
                py: 6,
                mb: 2,
                border: '1px solid #d1d5db',
                borderRadius: '8px',
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    mb: needsToConfirmSectionsList.length ? 1 : 0
                }}>
                    {isParentViewer
                        ? 'You cannot submit this form until the student profile is complete.'
                        : 'Parent submission is currently blocked until the student profile is complete.'}
                </Typography>

                {needsToConfirmSectionsList.length > 0 && (
                    <Box component="ul" sx={{
                        m: 0,
                        pl: 2
                    }}>
                        {needsToConfirmSectionsList.map(section => (
                            <li key={section}>
                                <Typography variant="body2">{section}</Typography>
                            </li>
                        ))}
                    </Box>
                )}
            </Alert>
        );
    };

    return (
        <Grid container flexGrow={1} minWidth="100%" maxHeight="100%">
            <Grid container item xs={12} overflow="auto" justifyContent="center">
                {
                    publicForm
                        ? <Grid item xs={12} sm={10} md={8} lg={6}>
                            {
                                !isLoading && error
                                    ? <ErrorView/>
                                    : <>
                                        <ParentBlockedBanner/>
                                        {schoolLogoComponent?.url && (
                                            <Box
                                                textAlign="center"
                                                sx={{
                                                    mx: {
                                                        xs: 2,
                                                        sm: 0
                                                    },
                                                    mt: 2,
                                                    mb: 1,
                                                    px: 3,
                                                    py: 6,
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                }}
                                            >
                                                <ImageWithConditionalRender
                                                    imageUrl={schoolLogoComponent.url}
                                                    alt="School logo"
                                                />
                                            </Box>
                                        )}
                                        <FormContent
                                            formRequest={formRequest}
                                            handleSubmit={handleSubmit}
                                            isLoading={isLoading}
                                            isSubmitting={isSubmitting}
                                            onDownloadAttachment={onDownloadAttachment}
                                            generalForm={generalForm}
                                            publicForm={publicForm}
                                            formAccessLevel={formAccessLevel}
                                            formRequestId={formRequestId}
                                            onEmailCheck={onEmailCheck}
                                            profileMustBeComplete={profileMustBeComplete}
                                            isProfileComplete={isProfileComplete}
                                            needsToConfirmSections={needsToConfirmSections}
                                            blockedForViewer={blockedForViewer}
                                            editMode={!!editMode}
                                            isParentView={isParentView}
                                        />
                                    </>
                            }
                        </Grid>
                        : <Grid item xs={12} sm={inPreview ? 12 : 10} md={inPreview ? 12 : 8} lg={inPreview ? 12 : 6}>
                            {!inPreview && (
                                <Box sx={{mb: 1}}>
                                    <BackButton/>
                                    <Divider sx={{mb: 2}}/>
                                </Box>
                            )}

                            {
                                !isLoading && error
                                    ? <ErrorView/>
                                    : <>
                                        <ParentBlockedBanner/>
                                        {schoolLogoComponent?.url && !inProfile && (
                                            <Box
                                                textAlign="center"
                                                sx={{
                                                    mb: 2,
                                                    px: 3,
                                                    py: inPreview ? 2 : 6,
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                }}
                                            >
                                                <ImageWithConditionalRender
                                                    imageUrl={schoolLogoComponent.url}
                                                    alt="School logo"
                                                />
                                            </Box>
                                        )}
                                        <Header/>
                                        <FormContent
                                            formRequest={formRequest}
                                            handleSubmit={handleSubmit}
                                            isLoading={isLoading}
                                            isSubmitting={isSubmitting}
                                            onDownloadAttachment={onDownloadAttachment}
                                            generalForm={generalForm}
                                            publicForm={publicForm}
                                            formAccessLevel={formAccessLevel}
                                            formRequestId={formRequestId}
                                            onEmailCheck={onEmailCheck}
                                            profileMustBeComplete={profileMustBeComplete}
                                            isProfileComplete={isProfileComplete}
                                            needsToConfirmSections={needsToConfirmSections}
                                            blockedForViewer={blockedForViewer}
                                            editMode={!!editMode}
                                        />
                                    </>
                            }
                        </Grid>
                }
            </Grid>
        </Grid>
    );
};

FormRequest.propTypes = {
    publicForm: PropTypes.bool,
    generalForm: PropTypes.bool,
    onSubmit: PropTypes.func,
    onDownloadAttachment: PropTypes.func,
    formRequest: PropTypes.object,
    isLoading: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    error: PropTypes.object,
    navigateBackRoute: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            pathname: PropTypes.string,
            state: PropTypes.object,
        }),
    ]).isRequired,
    formAccessLevel: PropTypes.number,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func,
    profileMustBeComplete: PropTypes.bool,
    isProfileComplete: PropTypes.bool,
    needsToConfirmSections: PropTypes.string,
    readOnly: PropTypes.bool,
    isParentView: PropTypes.bool,
    isElevated: PropTypes.bool,
    assigneeOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })),
    selectedAssigneeId: PropTypes.string,
    onAssigneeChange: PropTypes.func,
    formAudience: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    editMode: PropTypes.bool,
    editModeHeader: PropTypes.node,
};

FormRequest.defaultProps = {
    publicForm: false,
    generalForm: false,
    isLoading: false,
    isSubmitting: false,
    isParentView: false,
};

function useConfirmPaymentOnReturn(formRequestId) {
    const [verifySession] = useVerifyFormPaymentSessionMutation();
    const verified = useRef(null);

    useEffect(() => {
        if (!formRequestId) return;

        const params = new URLSearchParams(window.location.search);

        if (params.get('paid') !== '1') return;

        const sessionId = params.get('session_id');

        if (!sessionId || verified.current === sessionId) return;

        verified.current = sessionId;

        verifySession({formRequestId, sessionId}).finally(() => {
            params.delete('paid');
            params.delete('session_id');

            const query = params.toString();

            window.history.replaceState(
                window.history.state,
                '',
                window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
        });
    }, [formRequestId, verifySession]);
}

function FormContent({
                         isLoading,
                         isSubmitting,
                         formRequest,
                         handleSubmit,
                         onDownloadAttachment,
                         publicForm,
                         generalForm,
                         formAccessLevel,
                         formRequestId,
                         onEmailCheck,
                         profileMustBeComplete,
                         isProfileComplete,
                         needsToConfirmSections,
                         blockedForViewer,
                         editMode
                     }) {
    useConfirmPaymentOnReturn(formRequestId);

    if (isLoading) {
        return (
            <Box sx={{
                display: 'grid',
                gap: 2
            }}>
                {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={120}/>
                ))}
                <Skeleton variant="rounded" width={120} height={36} sx={{ml: 'auto'}}/>
            </Box>
        );
    }

    if (!formRequest?.schema) return null;

    const isParentContactForm =
        Array.isArray(formRequest?.schema) &&
        formRequest.schema.some(c => c.key === 'parentEmail');
    const linkedToStudents = !!(formRequest?.linkToStudents ?? formRequest?.LinkToStudents);
    const linkedToEmployees = !!(formRequest?.linkToEmployees ?? formRequest?.LinkToEmployees);

    // AD-2695: a submitted response opened with 'Edit Response' can be changed.
    // AD-2742: a parent whose child's profile is not complete can still fill in the form; Submit shows the pop-up.
    const readOnly = !editMode && (
        (formRequest.status === FORM_REQUEST_STATUSES.submitted) ||
        // Maria, 18 October 2026: a form waiting for someone always has a Submit button, including one whose email
        // has not gone yet ('Created') or could not be sent. A communication stays read-only (below).
        (!publicForm &&
            !generalForm &&
            ![FORM_REQUEST_STATUSES.sent, FORM_REQUEST_STATUSES.created, FORM_REQUEST_STATUSES.failedToSend]
                .includes(formRequest.status)));

    return (
        <Box>
            <FormBuilder
                schema={formRequest.schema}
                placeholderValues={{
                    ...formRequest.placeholderValues,
                    formRequestStatus: formRequest?.status
                }}
                submitMode={true}
                handleSubmit={handleSubmit}
                handleDownloadAttachment={onDownloadAttachment}
                data={formRequest.data}
                readOnly={readOnly || formRequest.isCommunication}
                publicForm={publicForm}
                isCommunication={formRequest.isCommunication}
                accessLevel={formAccessLevel}
                linkedToStudents={linkedToStudents}
                linkedToEmployees={linkedToEmployees}
                formRequestId={formRequestId}
                onEmailCheck={
                    isParentContactForm
                        ? onEmailCheck
                        : undefined
                }
                profileMustBeComplete={profileMustBeComplete}
                isProfileComplete={isProfileComplete}
                needsToConfirmSections={needsToConfirmSections}
                blockedForViewer={blockedForViewer}
                profileStudentId={formRequest.studentId}
            />
            <Backdrop open={isSubmitting} sx={{
                color: '#fff',
                zIndex: theme => theme.zIndex.modal + 1
            }}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
}

FormContent.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    formRequest: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onDownloadAttachment: PropTypes.func,
    publicForm: PropTypes.bool,
    generalForm: PropTypes.bool,
    formAccessLevel: PropTypes.number,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func,
    profileMustBeComplete: PropTypes.bool,
    isProfileComplete: PropTypes.bool,
    needsToConfirmSections: PropTypes.string,
    readOnly: PropTypes.bool,
    blockedForViewer: PropTypes.bool,
    editMode: PropTypes.bool,

};

FormContent.defaultProps = {
    onDownloadAttachment: () => {
    },
    publicForm: false,
    generalForm: false
};

// AD-2742: only staff looking at the form see the banner; a parent is told on Submit.
export const showsParentBlockedBanner = ({isLoading, submissionBlocked, isParentViewer}) =>
    !isLoading && !!submissionBlocked && !isParentViewer;

export default FormRequest;
